import { useState } from "react";

const initialCategories = [
  { id: 1, name: "Peptides", description: "Research peptides sold as lyophilized powder or solution." },
  { id: 2, name: "Compounds", description: "General research compounds and chelates." },
  { id: 3, name: "Kits", description: "Bundled multi-vial research kits." },
  { id: 4, name: "Accessories", description: "Bacteriostatic water, vials, and lab accessories." },
  { id: 5, name: "Uncategorized", description: "Default category for unsorted products." },
];

export default function Categories() {
  const [categories, setCategories] = useState(initialCategories);
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const emptyForm = { name: "", description: "" };
  const [formData, setFormData] = useState(emptyForm);

  const openAddModal = () => {
    setEditingCategory(null);
    setFormData(emptyForm);
    setIsModalOpen(true);
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setFormData({ name: category.name, description: category.description });
    setIsModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (editingCategory) {
      setCategories((prev) =>
        prev.map((c) => (c.id === editingCategory.id ? { ...c, ...formData } : c))
      );
    } else {
      setCategories((prev) => [...prev, { id: Date.now(), ...formData }]);
    }

    setIsModalOpen(false);
  };

  const confirmDelete = () => {
    setCategories((prev) => prev.filter((c) => c.id !== deleteTarget.id));
    setDeleteTarget(null);
  };

  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-gray-800 font-semibold text-2xl">Categories</h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage the categories available when adding products
          </p>
        </div>

        <button
          onClick={openAddModal}
          className="flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white font-medium text-sm rounded-md px-5 py-2.5 transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Add Category
        </button>
      </div>

      <div className="relative max-w-md mb-6">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5" />
          <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          placeholder="Search categories..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-white border border-gray-300 rounded-md pl-9 pr-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-500"
        />
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left">
                <th className="px-6 py-4 text-gray-500 font-medium">Category</th>
                <th className="px-6 py-4 text-gray-500 font-medium">Description</th>
                <th className="px-6 py-4 text-gray-500 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {filteredCategories.map((category) => (
                <tr
                  key={category.id}
                  className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-teal-50 text-teal-600 flex items-center justify-center flex-shrink-0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                          <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                          <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                          <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                        </svg>
                      </div>
                      <p className="text-gray-800 font-medium">{category.name}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-500">{category.description}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-4">
                      <button
                        onClick={() => openEditModal(category)}
                        className="text-teal-600 hover:text-teal-700 font-medium text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setDeleteTarget(category)}
                        className="text-red-500 hover:text-red-600 font-medium text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {filteredCategories.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-6 py-10 text-center text-gray-400">
                    No categories match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => setIsModalOpen(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg w-full max-w-md">
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
                <h2 className="text-gray-800 font-semibold text-lg">
                  {editingCategory ? "Edit Category" : "Add Category"}
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-700"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="px-6 py-6 flex flex-col gap-5">
                <div>
                  <label className="block text-gray-600 text-sm font-medium mb-2">
                    Category Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-50 border border-gray-300 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-gray-600 text-sm font-medium mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className="w-full bg-gray-50 border border-gray-300 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-500 resize-y"
                  />
                </div>

                <div className="flex items-center gap-3 mt-2">
                  <button
                    type="submit"
                    className="flex-1 bg-teal-500 hover:bg-teal-600 text-white font-semibold text-sm rounded-md py-2.5 transition-colors"
                  >
                    {editingCategory ? "Save Changes" : "Add Category"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="flex-1 border border-gray-300 text-gray-600 font-medium text-sm rounded-md py-2.5 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {deleteTarget && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => setDeleteTarget(null)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg w-full max-w-sm p-6">
              <h3 className="text-gray-800 font-semibold text-lg">Delete Category</h3>
              <p className="text-gray-500 text-sm mt-2">
                Are you sure you want to delete "{deleteTarget.name}"? Products linked to this category won't be deleted, but will lose this category tag.
              </p>

              <div className="flex items-center gap-3 mt-6">
                <button
                  onClick={confirmDelete}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold text-sm rounded-md py-2.5 transition-colors"
                >
                  Delete
                </button>
                <button
                  onClick={() => setDeleteTarget(null)}
                  className="flex-1 border border-gray-300 text-gray-600 font-medium text-sm rounded-md py-2.5 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}